import { useCallback, useEffect, useState } from "react";
import { fetchFoodRetailSourceAccessStrategy } from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";

const FoodAraCityGateStrategySection = ({ sourceKey }) => {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const loadStrategy = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setStrategy(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodRetailSourceAccessStrategy(sourceKey);
      setStrategy(payload);
    } catch (error) {
      setErrorMessage(error.message || `Failed to load ${sourceLabel} access strategy.`);
      setStrategy(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey, sourceLabel]);

  useEffect(() => {
    loadStrategy();
  }, [loadStrategy]);

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Access Strategy</h2>
          <p className="muted">
            Approved PoC path for {sourceLabel} collection. Read-only — does not run ingestion.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadStrategy} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !strategy ? <p className="muted">Loading strategy…</p> : null}

      {strategy ? (
        <>
          <div className="food-retail-ingestion-page__kpi-row">
            <div>
              <span className="muted">Selected strategy</span>
              <strong>{strategy.selectedStrategy}</strong>
            </div>
            <div>
              <span className="muted">Status</span>
              <strong>{strategy.strategyStatus}</strong>
            </div>
            <div>
              <span className="muted">Live fetch allowed</span>
              <strong>{strategy.liveFetchAllowed ? "Yes" : "No"}</strong>
            </div>
            <div>
              <span className="muted">Fixture mode allowed</span>
              <strong>{strategy.fixtureModeAllowed ? "Yes" : "No"}</strong>
            </div>
            <div>
              <span className="muted">Scheduled ingestion</span>
              <strong>{strategy.scheduledIngestionAllowed ? "Yes" : "No"}</strong>
            </div>
          </div>

          {strategy.cityGateWarning ? (
            <p className="muted food-retail-ingestion-page__hint">{strategy.cityGateWarning}</p>
          ) : null}

          {strategy.notes ? (
            <p className="muted food-retail-ingestion-page__hint">{strategy.notes}</p>
          ) : null}

          <div className="food-retail-ingestion-page__hint">
            <strong>Recommended fixtures (Milestone 4)</strong>
            <ul>
              {(strategy.recommendedFixtureNames || []).map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>

          {(strategy.existingFixtureNames || []).length ? (
            <div className="food-retail-ingestion-page__hint">
              <strong>Available fixtures</strong>
              <ul>
                {strategy.existingFixtureNames.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="food-retail-ingestion-page__hint">
            <strong>Blocked modes</strong>
            <p>{(strategy.blockedModes || []).join(", ")}</p>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default FoodAraCityGateStrategySection;
